import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { Search } from "lucide-react";
import { VariantChooser } from "./VariantChooser";
import { CatalogCard } from "./CatalogCard";
import { DesignDetail } from "./DesignDetail";
import { SelectionBar } from "./SelectionBar";
import { Checkbox } from "@/components/ui/checkbox";
import api0 from "@/lib/api";
import { useSearchParams } from "react-router-dom";

const PAGE = 24;

export const CatalogBrowser = ({ api, onAdd }) => {
  const [v, setV] = useState({ color: null, purity: null });
  const [category, setCategory] = useState("all");
  const [availability, setAvailability] = useState("all");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [picked, setPicked] = useState([]);
  const [allKeys, setAllKeys] = useState(null);
  const isAdmin = api.mode === "admin";
  // CS 22.15 -- "Asht Lite" checkbox: same effective-AL rule as the card badge; persisted as ?asht_lite=1.
  const [searchParams, setSearchParams] = useSearchParams();
  const alOnly = searchParams.get("asht_lite") === "1";
  const setAlOnly = (on) => setSearchParams((sp) => {
    const n = new URLSearchParams(sp);
    if (on) n.set("asht_lite", "1"); else n.delete("asht_lite");
    return n;
  }, { replace: true });
  const clearFilters = () => { setCategory("all"); setAvailability("all"); setQ(""); setQDebounced(""); setAlOnly(false); };

  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [v.color, v.purity, category, availability, qDebounced, alOnly]);

  const params = useMemo(() => {
    const p = { page, page_size: PAGE, availability };
    if (v.color) p.color = v.color;
    if (v.purity) p.purity = v.purity;
    if (category !== "all") p.category = category;
    if (qDebounced.trim()) p.q = qDebounced.trim();
    if (alOnly) p.al_only = true;
    return p;
  }, [page, availability, v.color, v.purity, category, qDebounced, alOnly]);

  useEffect(() => {
    let stale = false;  // CS 22.15 -- only the latest filter/page request may update the grid
    api.catalog(params).then((d) => { if (!stale) setData(d); })
      .catch((e) => { if (!stale) toast.error(formatApiError(e.response?.data?.detail) || "Failed to load catalog"); });
    return () => { stale = true; };
  }, [api, params]);

  const filterParams = useMemo(() => { const { page: _p, page_size: _s, ...rest } = params; return rest; }, [params]);
  useEffect(() => { setAllKeys(null); }, [filterParams]);
  const toggle = (k) => setPicked((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const allSelected = !!allKeys && allKeys.length > 0 && allKeys.every((k) => picked.includes(k));
  const toggleAll = async () => {
    try {
      const keys = allKeys || (await api0.get("/master-catalog/catalog/keys", { params: filterParams })).data.keys;
      setAllKeys(keys);
      const every = keys.every((k) => picked.includes(k));
      setPicked((s) => (every ? s.filter((k) => !keys.includes(k)) : [...new Set([...s, ...keys])]));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not select all");
    }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;
  return (
    <div className="space-y-5" data-testid="mc-browser">
      <div className="luxe-card p-4 space-y-4">
        <VariantChooser color={v.color} purity={v.purity} onChange={setV} testid="mc-global-variant" allowUploaded />
        <p className="text-[11px] text-slate-500" data-testid="mc-global-variant-note">
          {v.color || v.purity ? "Designs shown in your chosen variant; untouched choices stay as uploaded." : "Each design is shown in its uploaded colour and purity. Choose a variant to view all designs that way."}
        </p>
        <div className="flex flex-wrap gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-48" data-testid="mc-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {(data?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={availability} onValueChange={setAvailability}>
            <SelectTrigger className="w-44" data-testid="mc-filter-availability"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="ready">Ready Stock</SelectItem>
              <SelectItem value="make_to_order">Make to Order</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Style No" className="pl-9" data-testid="mc-filter-search" />
          </div>
          <label className="flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-background text-sm cursor-pointer select-none" data-testid="mc-filter-al-label">
            <Checkbox checked={alOnly} onCheckedChange={(c) => setAlOnly(c === true)} data-testid="mc-filter-al" />
            Asht Lite
          </label>
        </div>
        <div className="flex items-center gap-4">
          {isAdmin && data?.total > 0 && (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="mc-select-all" />
              Select all {data.total} matching
            </label>
          )}
          <div className="text-xs text-slate-500" data-testid="mc-result-count">{data ? `${data.total} designs` : "Loading…"}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4 items-stretch" data-testid="mc-grid">
        {(data?.items || []).map((c) => <CatalogCard key={c.design_key} c={c} onOpen={setOpen} admin={isAdmin} selected={picked.includes(c.design_key)} onToggle={isAdmin ? toggle : null} />)}
      </div>
      {data && data.total === 0 && (
        <div className="flex items-center gap-3 text-sm text-slate-400" data-testid="mc-empty">
          No designs match these filters.
          <Button variant="outline" size="sm" onClick={clearFilters} data-testid="mc-empty-clear">Clear filters</Button>
        </div>
      )}

      <div className="flex items-center justify-center gap-3">
        <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="mc-prev-page">Previous</Button>
        <span className="text-xs text-slate-500" data-testid="mc-page-indicator">Page {page} / {pages}</span>
        <Button variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="mc-next-page">Next</Button>
      </div>

      {isAdmin && <SelectionBar keys={picked} variant={v} onClear={() => setPicked([])} />}

      {open && (
        <DesignDetail key={open.design_key} api={api} card={open} initial={v} onClose={() => setOpen(null)}
          onAdd={onAdd ? async (body) => { await onAdd(body); setOpen(null); } : null} />
      )}
    </div>
  );
};
